import React, { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { 
  ArrowLeft,
  Search, 
  Filter, 
  RefreshCw,
  Eye,
  CheckCircle,
  XCircle,
  Clock,
  Award,
  FileText,
  Calendar,
  DollarSign,
  Building2,
  Loader2,
  AlertCircle
} from 'lucide-react'
import apiService from '../../services/api'
import { toast } from 'react-toastify'

interface GovProposal {
  id: string
  message: string
  status: 'submitted' | 'under-review' | 'accepted' | 'rejected'
  submittedAt: string
  reviewedAt?: string
  reviewNotes?: string
  GovRfq: {
    id: string
    title: string
    description: string
    deadline: string
    budgetRange: string
  }
  vendor: {
    id: string
    companyName: string
    firstName: string
    lastName: string
    email: string
    avatar?: string
  }
  attachments: string[]
}

const GovProposalsPage: React.FC = () => {
  const [proposals, setProposals] = useState<GovProposal[]>([])
  const [loading, setLoading] = useState(true)
  const [searchTerm, setSearchTerm] = useState('')
  const [statusFilter, setStatusFilter] = useState('all')
  const [rfqFilter, setRfqFilter] = useState('all')
  const [error, setError] = useState<string | null>(null)
  const [rfqs, setRfqs] = useState<any[]>([])

  useEffect(() => {
    fetchProposals()
    fetchRfqs()
  }, [])

  const fetchProposals = async () => {
    try {
      setLoading(true)
      setError(null)
      
      const response = await apiService.get('/api/gov/rfqs/proposals') as any
      console.log('GovProposalsPage - API Response:', response)
      
      if (response?.success) {
        // The API returns { data: { proposals: [...], pagination: {...} } }
        const proposalsData = response.data?.proposals || []
        console.log('GovProposalsPage - Proposals data:', proposalsData)
        setProposals(proposalsData)
      } else {
        console.error('GovProposalsPage - API returned error:', response)
        setError('Failed to fetch proposals')
        toast.error('Failed to fetch proposals')
      }
    } catch (err) {
      console.error('Error fetching proposals:', err)
      setError('Failed to fetch proposals')
      toast.error('Failed to fetch proposals')
    } finally {
      setLoading(false)
    }
  }

  const fetchRfqs = async () => {
    try {
      const response = await apiService.get('/api/gov/rfqs/my') as any
      console.log('GovProposalsPage - RFQs API Response:', response)
      if (response?.success) {
        // The API returns { data: { rfqs: [...], pagination: {...} } }
        const rfqsData = response.data?.rfqs || []
        console.log('GovProposalsPage - RFQs data:', rfqsData)
        setRfqs(rfqsData)
      }
    } catch (err) {
      console.error('Error fetching RFQs:', err)
    }
  }

  const filteredProposals = (proposals || []).filter(proposal => {
    const matchesSearch = proposal.vendor?.companyName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         proposal.GovRfq?.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         proposal.message?.toLowerCase().includes(searchTerm.toLowerCase())
    
    const matchesStatusFilter = statusFilter === 'all' || proposal.status === statusFilter
    const matchesRfqFilter = rfqFilter === 'all' || proposal.GovRfq?.id === rfqFilter
    
    return matchesSearch && matchesStatusFilter && matchesRfqFilter
  })

  const getStatusBadge = (status: string) => {
    const statusConfig = {
      submitted: { color: 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200', icon: Clock },
      'under-review': { color: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200', icon: Eye },
      accepted: { color: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200', icon: CheckCircle },
      rejected: { color: 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200', icon: XCircle }
    }
    
    const config = statusConfig[status as keyof typeof statusConfig] || statusConfig.submitted
    const Icon = config.icon
    
    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${config.color}`}>
        <Icon className="w-3 h-3 mr-1" />
        {status.replace('-', ' ').replace(/\b\w/g, l => l.toUpperCase())}
      </span>
    )
  }

  const handleUpdateStatus = async (proposalId: string, status: string, reviewNotes?: string) => {
    try {
      const response = await apiService.put(`/api/gov/proposals/${proposalId}/status`, {
        status,
        reviewNotes
      }) as any
      
      if (response?.success) {
        toast.success(`Proposal ${status.replace('-', ' ')} successfully`)
        fetchProposals()
      } else {
        toast.error('Failed to update proposal status')
      }
    } catch (err) {
      console.error('Error updating proposal status:', err)
      toast.error('Failed to update proposal status')
    }
  }

  const ProposalCard: React.FC<{ proposal: GovProposal }> = ({ proposal }) => (
    <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border dark:border-gray-700 p-6 hover:shadow-md transition-shadow">
      <div className="flex items-start justify-between mb-4">
        <div className="flex items-start space-x-4">
          <div className="w-12 h-12 bg-primary-100 dark:bg-primary-900 rounded-lg flex items-center justify-center flex-shrink-0">
            {proposal.vendor.avatar ? (
              <img
                src={proposal.vendor.avatar}
                alt={proposal.vendor.companyName}
                className="w-12 h-12 rounded-lg object-cover"
              />
            ) : (
              <Building2 className="w-6 h-6 text-primary-600 dark:text-primary-400" />
            )}
          </div>
          
          <div className="flex-1">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-1">
              {proposal.vendor.companyName}
            </h3>
            <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">
              {proposal.vendor.firstName} {proposal.vendor.lastName}
            </p>
            <p className="text-sm text-gray-500 dark:text-gray-400">
              {proposal.vendor.email}
            </p>
          </div>
        </div>
        
        {getStatusBadge(proposal.status)}
      </div>
      
      <div className="mb-4">
        <h4 className="font-medium text-gray-900 dark:text-gray-100 mb-2">
          RFQ: {proposal.GovRfq.title}
        </h4>
        <p className="text-sm text-gray-600 dark:text-gray-400 line-clamp-2">
          {proposal.message}
        </p>
      </div>
      
      <div className="grid grid-cols-2 gap-4 mb-4">
        <div className="flex items-center text-sm text-gray-600 dark:text-gray-400">
          <Calendar className="w-4 h-4 mr-2" />
          Submitted {new Date(proposal.submittedAt).toLocaleDateString()}
        </div>
        <div className="flex items-center text-sm text-gray-600 dark:text-gray-400">
          <DollarSign className="w-4 h-4 mr-2" />
          {proposal.GovRfq.budgetRange}
        </div>
        {proposal.attachments.length > 0 && (
          <div className="flex items-center text-sm text-gray-600 dark:text-gray-400">
            <FileText className="w-4 h-4 mr-2" />
            {proposal.attachments.length} attachment(s)
          </div>
        )}
        <div className="flex items-center text-sm text-gray-600 dark:text-gray-400">
          <Clock className="w-4 h-4 mr-2" />
          Deadline: {new Date(proposal.GovRfq.deadline).toLocaleDateString()}
        </div>
      </div>
      
      {proposal.reviewNotes && (
        <div className="mb-4 p-3 bg-gray-50 dark:bg-gray-700 rounded-lg">
          <p className="text-sm text-gray-700 dark:text-gray-300">
            <strong>Review Notes:</strong> {proposal.reviewNotes}
          </p>
        </div>
      )}
      
      <div className="flex items-center justify-between pt-4 border-t dark:border-gray-700">
        <div className="flex items-center space-x-2">
          <button
            onClick={() => window.open(`/gov/proposals/${proposal.id}`, '_blank')}
            className="text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300 text-sm font-medium"
          >
            <Eye className="w-4 h-4 inline mr-1" />
            View Details
          </button>
        </div>
        
        {proposal.status === 'submitted' && (
          <div className="flex items-center space-x-2">
            <button
              onClick={() => handleUpdateStatus(proposal.id, 'under-review', 'Under review')}
              className="text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 text-sm font-medium"
            >
              <Eye className="w-4 h-4 inline mr-1" />
              Start Review
            </button>
            <button
              onClick={() => handleUpdateStatus(proposal.id, 'accepted', 'Proposal accepted')}
              className="text-green-600 dark:text-green-400 hover:text-green-700 dark:hover:text-green-300 text-sm font-medium"
            >
              <CheckCircle className="w-4 h-4 inline mr-1" />
              Accept
            </button>
            <button
              onClick={() => {
                const notes = prompt('Rejection reason:')
                if (notes) {
                  handleUpdateStatus(proposal.id, 'rejected', notes)
                }
              }}
              className="text-red-600 dark:text-red-400 hover:text-red-700 dark:hover:text-red-300 text-sm font-medium"
            >
              <XCircle className="w-4 h-4 inline mr-1" />
              Reject
            </button>
          </div>
        )}
        
        {proposal.status === 'under-review' && (
          <div className="flex items-center space-x-2">
            <button
              onClick={() => handleUpdateStatus(proposal.id, 'accepted', 'Proposal accepted after review')}
              className="text-green-600 dark:text-green-400 hover:text-green-700 dark:hover:text-green-300 text-sm font-medium"
            >
              <Award className="w-4 h-4 inline mr-1" />
              Award Contract
            </button>
            <button
              onClick={() => {
                const notes = prompt('Rejection reason:')
                if (notes) {
                  handleUpdateStatus(proposal.id, 'rejected', notes)
                }
              }}
              className="text-red-600 dark:text-red-400 hover:text-red-700 dark:hover:text-red-300 text-sm font-medium"
            >
              <XCircle className="w-4 h-4 inline mr-1" />
              Reject
            </button>
          </div>
        )}
      </div>
    </div>
  )

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      {/* Header */}
      <div className="bg-white dark:bg-gray-800 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex items-center space-x-4 mb-6">
            <Link 
              to="/gov/dashboard"
              className="flex items-center text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-100"
            >
              <ArrowLeft className="h-5 w-5 mr-2" />
              Back to Dashboard
            </Link>
          </div>

          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900 dark:text-gray-100">
                Review Proposals
              </h1>
              <p className="mt-2 text-gray-600 dark:text-gray-400">
                Review and evaluate vendor proposals for your RFQs
              </p>
            </div>
            
            <div className="mt-4 lg:mt-0">
              <button
                onClick={fetchProposals}
                disabled={loading}
                className="btn btn-secondary flex items-center space-x-2"
              >
                <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
                <span>Refresh</span>
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Search and Filters */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border dark:border-gray-700 p-6 mb-8">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between space-y-4 md:space-y-0 md:space-x-4">
            <div className="flex-1">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                <input
                  type="text"
                  placeholder="Search proposals by vendor, RFQ title, or message..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="input pl-10"
                />
              </div>
            </div>
            
            <div className="flex items-center space-x-4">
              <div className="flex items-center space-x-2">
                <Filter className="w-4 h-4 text-gray-400" />
                <select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  className="input w-auto"
                >
                  <option value="all">All Status</option>
                  <option value="submitted">Submitted</option>
                  <option value="under-review">Under Review</option>
                  <option value="accepted">Accepted</option>
                  <option value="rejected">Rejected</option>
                </select>
              </div>
              
              <div className="flex items-center space-x-2">
                <FileText className="w-4 h-4 text-gray-400" />
                <select
                  value={rfqFilter}
                  onChange={(e) => setRfqFilter(e.target.value)}
                  className="input w-auto"
                >
                  <option value="all">All RFQs</option>
                  {(rfqs || []).map((rfq) => (
                    <option key={rfq.id} value={rfq.id}>
                      {rfq.title}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>
        </div>

        {/* Results */}
        <div className="mb-6">
          <p className="text-sm text-gray-600 dark:text-gray-400">
            Showing {filteredProposals.length} of {proposals.length} proposals
          </p>
        </div>

        {/* Proposals Grid */}
        {loading ? (
          <div className="text-center py-12">
            <div className="flex flex-col items-center">
              <Loader2 className="w-8 h-8 text-primary-600 animate-spin mb-4" />
              <p className="text-gray-600 dark:text-gray-400">Loading proposals...</p>
            </div>
          </div>
        ) : error ? (
          <div className="text-center py-12">
            <div className="flex flex-col items-center">
              <AlertCircle className="w-8 h-8 text-red-500 mb-4" />
              <p className="text-red-600 dark:text-red-400 mb-4">{error}</p>
              <button
                onClick={fetchProposals}
                className="btn btn-primary"
              >
                Try Again
              </button>
            </div>
          </div>
        ) : filteredProposals.length === 0 ? (
          <div className="text-center py-12">
            <div className="flex flex-col items-center">
              <FileText className="w-8 h-8 text-gray-400 mb-4" />
              <p className="text-gray-600 dark:text-gray-400 mb-4">
                {searchTerm || statusFilter !== 'all' || rfqFilter !== 'all'
                  ? 'No proposals match your search criteria.' 
                  : 'No proposals have been submitted to your RFQs yet.'}
              </p>
              {!searchTerm && statusFilter === 'all' && rfqFilter === 'all' && (
                <Link
                  to="/gov/rfqs/create"
                  className="btn btn-primary"
                >
                  Create Your First RFQ
                </Link>
              )}
            </div>
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {filteredProposals.map((proposal) => (
              <ProposalCard key={proposal.id} proposal={proposal} />
            ))}
          </div>
        )}
      </div>
    </div>
  )
}

export default GovProposalsPage